import axiosInstance, { axiosInstanceCached } from '@/utils/axios';
import { handleApiError, deleteDataCache } from '@/utils/helpers';
import mockData from './mock/aiCommentary.json';

// /admin/push_to_prod_ai_config

// Mock mode flag - set to false when real API is available
const USE_MOCK = false;

// Mock delay to simulate API latency
const mockDelay = (ms = 500) => {
  return new Promise((resolve) => {
    return setTimeout(resolve, ms);
  });
};

//* GET All AI Commentary Configs
export const getAICommentaryList = async () => {
  if (USE_MOCK) {
    await mockDelay();
    return {
      status: 200,
      data: mockData,
    };
  }

  let response = {};

  try {
    response = await axiosInstance.post(`/admin/list_ai_configs`);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* GET Single AI Commentary Config by ID
export const getAICommentary = async (payload) => {
  if (USE_MOCK) {
    await mockDelay();
    const config = mockData.find((c) => {
      return c.id === parseInt(payload.id);
    });
    if (config) {
      return {
        status: 200,
        data: config,
      };
    }
    return {
      status: 404,
      data: { error: { msg: 'Config not found' } },
    };
  }

  let response = {};

  try {
    response = await axiosInstance.post(`/admin/get_ai_config`, payload);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* POST Create new AI Commentary Config
export const createAICommentary = async (payload) => {
  if (USE_MOCK) {
    await mockDelay();
    const newConfig = {
      id:
        Math.max(
          ...mockData.map((c) => {
            return c.id;
          })
        ) + 1,
      ...payload,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    mockData.push(newConfig);
    return {
      status: 201,
      data: newConfig,
    };
  }

  let response = {};

  try {
    response = await axiosInstance.post(`/admin/create_ai_config`, payload);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* PUT Update existing AI Commentary Config
export const updateAICommentary = async (payload) => {
  if (USE_MOCK) {
    await mockDelay();
    const index = mockData.findIndex((c) => {
      return c.id === parseInt(payload.id);
    });
    if (index !== -1) {
      mockData[index] = {
        ...mockData.configs[index],
        ...payload,
        updated_at: new Date().toISOString(),
      };
      return {
        status: 200,
        data: mockData[index],
      };
    }
    return {
      status: 404,
      data: { error: { msg: 'Config not found' } },
    };
  }

  let response = {};

  try {
    response = await axiosInstance.post(`/admin/update_ai_config`, payload);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* POST Publish AI Commentary Config
// @ Payload should contain {"id":"config_uuid"} OR
// @ If rolling back to a previous version {"id":"config_uuid", "version":#version}
export const publishAICommentary = async (payload) => {
  if (USE_MOCK) {
    await mockDelay();
    const index = mockData.findIndex((c) => {
      return c.id === parseInt(payload.id);
    });
    if (index !== -1) {
      mockData[index] = {
        ...mockData[index],
        ...payload,
        published: true,
        published_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      return {
        status: 200,
        data: mockData[index],
      };
    }
    return {
      status: 404,
      data: { error: { msg: 'Config not found' } },
    };
  }

  let response = {};

  try {
    response = await axiosInstance.post(`/admin/publish_ai_config`, payload);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* DELETE AI Commentary Config
export const deleteAICommentary = async (payload) => {
  if (USE_MOCK) {
    await mockDelay();
    const index = mockData.findIndex((c) => {
      return c.id === parseInt(payload.id);
    });
    if (index !== -1) {
      mockData.splice(index, 1);
      return {
        status: 200,
        data: { success: true },
      };
    }
    return {
      status: 404,
      data: { error: { msg: 'Config not found' } },
    };
  }

  let response = {};

  try {
    response = await axiosInstance.post(`/admin/delete_ai_config`, payload);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

// @ Default no body pushes all to prod
// @ If body is provided, it will push the specific config to prod
// @ { id: config_uuid }
export const pushToProdAIConfig = async (payload) => {
  let response = {};

  try {
    response = await axiosInstance.post(`/admin/push_to_prod_ai_config`, payload);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

export const getIBMModels = async () => {
  let response = {};
  try {
    response = await axiosInstance.post(`/admin/get_ibm_ai_models`);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

export const getAIConfigVersion = async (payload) => {
  let response = {};
  try {
    response = await axiosInstance.post(`/admin/get_ai_config_version`, payload);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};
